import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import SEO, { SEOConfigs } from '../components/SEO';
import { 
  Check, Zap, Crown, ArrowRight, 
  Clock, Bell, Shield,
  Sparkles, Rocket
} from 'lucide-react';

const PricingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const yearlyCardRef = useRef(null);

  // LANSMAN FİYATLARI
  const monthlyPrice = 590;
  const quarterlyPrice = 1490;
  const yearlyPrice = 4990;
  
  // Hesaplamalar
  const quarterlyMonthly = Math.round(quarterlyPrice / 3); // ~497
  const yearlyMonthly = Math.round(yearlyPrice / 12); // ~416
  const quarterlyDiscount = Math.round((1 - quarterlyPrice / (monthlyPrice * 3)) * 100); // %16
  const yearlyDiscount = Math.round((1 - yearlyPrice / (monthlyPrice * 12)) * 100); // %30

  // Yıllık kart animasyonu
  useEffect(() => {
    if (yearlyCardRef.current) {
      setTimeout(() => {
        yearlyCardRef.current.classList.add('animate-glow');
      }, 300);
    }
  }, []);

  const handleGetStarted = (billing) => {
    if (user) {
      // Giriş yapmış kullanıcı -> Ödeme sayfasına
      navigate(`/payment?plan=pro&billing=${billing}`);
    } else {
      // Giriş yapmamış -> Kayıt sayfasına
      navigate(`/register?plan=pro&billing=${billing}`);
    }
  };

  return (
    <div className="min-h-screen bg-brand-dark py-8 px-4">
      <SEO {...SEOConfigs.pricing} />
      
      <div className="max-w-4xl mx-auto">

        {/* Header */}
        <div className="text-center mb-6">
          <Badge className="mb-2 bg-primary/20 text-primary border-primary/50 text-xs">
            <Sparkles className="h-3 w-3 mr-1" />
            Pro Üyelik
          </Badge>
          <h1 className="text-2xl md:text-3xl font-heading font-bold mb-2">
            Sinyalleri Kaçırma, <span className="text-gradient">Anında Haberdar Ol</span>
          </h1>
          <p className="text-slate-400 text-sm max-w-xl mx-auto mb-3">
            Telegram ve e-posta bildirimleri ile her fırsattan anında haberdar ol.
          </p>
          {/* Free vs Pro farkı - Acı noktası */}
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-slate-800/50 border border-slate-700 rounded-full">
            <span className="text-amber-400 text-sm">🔒</span>
            <span className="text-xs text-slate-400">
              Ücretsiz hesapta bildirimler kapalı. <span className="text-primary font-medium">Pro ile Telegram&apos;dan anında al.</span>
            </span>
          </div>
        </div>

        {/* 3 Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-5 max-w-4xl mx-auto mb-8 items-stretch pt-5">
          
          {/* AYLIK PLAN */}
          <Card className="border-slate-700 hover:border-slate-500 transition-all flex flex-col">
            <CardContent className="p-6 flex flex-col flex-1">
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2.5 rounded-lg bg-slate-800">
                    <Clock className="h-6 w-6 text-slate-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">Aylık</h3>
                    <p className="text-sm text-slate-500">Esnek</p>
                  </div>
                </div>
                <div className="mb-2">
                  <span className="text-4xl font-bold text-white">₺{monthlyPrice}</span>
                  <span className="text-slate-500 text-base"> / ay</span>
                </div>
                <p className="text-sm text-slate-500">Her ay faturalanır</p>
              </div>

              <ul className="space-y-3 mb-6 text-sm flex-1">
                <li className="flex items-start gap-2.5">
                  <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <span className="text-slate-300">Telegram&apos;dan anında sinyal al</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <span className="text-slate-300">Entry / TP / SL tam erişim</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <span className="text-slate-300">Stop değişince haber al</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <span className="text-slate-300">İstediğin zaman iptal</span>
                </li>
              </ul>

              <Button 
                className="w-full bg-slate-600 hover:bg-slate-500 text-white text-base h-12 mt-auto font-semibold"
                onClick={() => handleGetStarted('monthly')}
                data-testid="pricing-monthly-btn"
              >
                1 Ay Dene
                <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
            </CardContent>
          </Card>

          {/* 3 AYLIK PLAN - Popüler */}
          <Card className="border-primary/50 bg-primary/5 hover:border-primary transition-all relative flex flex-col">
            <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10">
              <Badge className="bg-primary text-black text-sm font-bold px-4 py-1.5 whitespace-nowrap">
                EN ÇOK TERCİH EDİLEN
              </Badge>
            </div>
            <CardContent className="p-6 flex flex-col flex-1">
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2.5 rounded-lg bg-primary/20">
                    <Zap className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg text-primary">3 Aylık</h3>
                    <p className="text-sm text-primary/80 font-medium">1 Ay Bedava!</p>
                  </div>
                </div>
                <div className="mb-2">
                  <span className="text-4xl font-bold text-white">₺{quarterlyPrice}</span>
                  <span className="text-slate-500 text-base"> / 3 ay</span>
                </div>
                <p className="text-sm text-primary font-medium">
                  Aylık ₺{quarterlyMonthly}&apos;ye gelir • %{quarterlyDiscount} tasarruf
                </p>
                <p className="text-sm text-amber-400 mt-1 font-medium">
                  🎁 1 Ay Bedava (toplam 4 ay erişim)
                </p>
              </div>

              <ul className="space-y-3 mb-6 text-sm flex-1">
                <li className="flex items-start gap-2.5">
                  <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <span className="text-slate-300">Telegram&apos;dan anında sinyal al</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <span className="text-slate-300">Entry / TP / SL tam erişim</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <span className="text-slate-300">Stop değişince haber al</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <span className="text-slate-300">1 ay bedava kazanç</span>
                </li>
              </ul>

              <Button 
                className="w-full bg-primary hover:bg-primary/90 text-black font-semibold text-base h-12 mt-auto"
                onClick={() => handleGetStarted('quarterly')}
                data-testid="pricing-quarterly-btn"
              >
                <Zap className="h-5 w-5 mr-2" />
                En İyi Seçim
              </Button>
            </CardContent>
          </Card>

          {/* YILLIK PLAN */}
          <Card 
            ref={yearlyCardRef}
            className="border-amber-500/50 bg-gradient-to-b from-amber-500/10 to-transparent hover:border-amber-400 transition-all relative flex flex-col"
          >
            <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10">
              <Badge className="bg-amber-500 text-black text-sm font-bold px-4 py-1.5 whitespace-nowrap">
                MAX TASARRUF
              </Badge>
            </div>
            <CardContent className="p-6 flex flex-col flex-1">
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2.5 rounded-lg bg-amber-500/20">
                    <Crown className="h-6 w-6 text-amber-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg text-amber-400">Yıllık</h3>
                    <p className="text-sm text-slate-500">3+ Ay Bedava</p>
                  </div>
                </div>
                <div className="mb-2">
                  <span className="text-4xl font-bold text-white">₺{yearlyPrice}</span>
                  <span className="text-slate-500 text-base"> / yıl</span>
                </div>
                <p className="text-sm text-amber-400">
                  Aylık ₺{yearlyMonthly} • %{yearlyDiscount} tasarruf
                </p>
              </div>

              <ul className="space-y-3 mb-6 text-sm flex-1">
                <li className="flex items-start gap-2.5">
                  <Check className="h-5 w-5 text-amber-400 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-300">Telegram&apos;dan anında sinyal al</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <Check className="h-5 w-5 text-amber-400 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-300">Entry / TP / SL tam erişim</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <Check className="h-5 w-5 text-amber-400 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-300">Stop değişince haber al</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <Check className="h-5 w-5 text-amber-400 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-300">3+ ay bedava kazanç</span>
                </li>
              </ul>

              <Button 
                className="w-full bg-amber-500 hover:bg-amber-400 text-black font-semibold text-base h-12 mt-auto"
                onClick={() => handleGetStarted('yearly')}
                data-testid="pricing-yearly-btn"
              >
                <Crown className="h-5 w-5 mr-2" />
                Maximum Tasarruf
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Güven Mesajı */}
        <Card className="mb-8 border-slate-700/50 bg-slate-900/30 max-w-3xl mx-auto">
          <CardContent className="p-5">
            <div className="flex items-center justify-center gap-2 text-center">
              <Check className="h-5 w-5 text-primary flex-shrink-0" />
              <p className="text-sm text-slate-400">
                <span className="text-white font-medium">Performansı gör, sonra karar ver</span> — tüm geçmiş sinyaller şeffaf şekilde sitede.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Güven Mesajları */}
        <div className="flex flex-wrap justify-center gap-6 text-sm text-slate-500 max-w-3xl mx-auto mb-6">
          <div className="flex items-center gap-2">
            <Shield className="h-4 w-4" />
            <span>Güvenli Ödeme</span>
          </div>
          <div className="flex items-center gap-2">
            <Bell className="h-4 w-4" />
            <span>Anlık Bildirimler</span>
          </div>
          <div className="flex items-center gap-2">
            <Zap className="h-4 w-4" />
            <span>Anında Aktivasyon</span>
          </div>
        </div>

        {/* Alt Güven Cümleleri */}
        <div className="flex flex-col items-center gap-2 text-sm text-slate-500 max-w-3xl mx-auto">
          <div className="flex items-center gap-2">
            <Check className="h-4 w-4 text-green-500" />
            <span>İstediğin zaman iptal edebilirsin.</span>
          </div>
          <div className="flex items-center gap-2">
            <Check className="h-4 w-4 text-green-500" />
            <span>Geçmiş performans tamamen şeffaf.</span>
          </div>
        </div>

        {/* Ücretsiz Başla */}
        {!user && (
          <div className="text-center mt-8 pt-8 border-t border-slate-800">
            <p className="text-slate-400 mb-3">
              Henüz karar veremedin mi?
            </p>
            <Button 
              variant="outline" 
              className="border-slate-600 text-slate-300 hover:bg-slate-800"
              onClick={() => navigate('/register')}
              data-testid="pricing-free-btn"
            >
              Önce Ücretsiz Dene
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        )}

      </div>
    </div>
  );
};

export default PricingPage;
